import { useState, type ReactNode } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  Bell,
  CalendarDays,
  ImageIcon,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  ShoppingBag,
  ShoppingCart,
  Sun,
  User,
  Users,
  X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTema } from "@/contexts/TemaContext";
import { useCarrito } from "@/contexts/CarritoContext";
import { useNotificaciones } from "@/hooks/useDatos";
import AvisoVerificarCorreo from "./AvisoVerificarCorreo";
import CarritoDrawer from "./CarritoDrawer";
import PanelNotificaciones from "./PanelNotificaciones";

/**
 * El orden no es decorativo: primero lo que se hace a diario —consultar,
 * reservar, comprar— y después lo que se visita de vez en cuando.
 */
const ENLACES = [
  { a: "/panel", texto: "Mi panel", Icono: LayoutDashboard },
  { a: "/reservar", texto: "Reservar", Icono: CalendarDays },
  { a: "/tienda", texto: "Tienda", Icono: ShoppingBag },
  { a: "/coaches", texto: "Coaches", Icono: Users },
  { a: "/instalaciones", texto: "Estudio", Icono: ImageIcon },
  { a: "/perfil", texto: "Perfil", Icono: User },
];

export default function Layout({ children }: { children: ReactNode }) {
  const { perfil, esStaff, salir } = useAuth();
  const { tema, alternar } = useTema();
  const { cantidadTotal, setAbierto } = useCarrito();
  const { sinLeer } = useNotificaciones();
  const navegar = useNavigate();

  const [menuAbierto, setMenuAbierto] = useState(false);
  const [notisAbiertas, setNotisAbiertas] = useState(false);

  const cerrarSesion = async () => {
    await salir();
    navegar("/");
  };

  return (
    <div className="pila" style={{ minHeight: "100dvh" }}>
      <header
        className="vidrio"
        style={{
          position: "sticky",
          top: 0,
          zIndex: "var(--z-nav)" as unknown as number,
          borderInline: "none",
          borderTop: "none",
        }}
      >
        <div
          className="contenedor fila entre"
          style={{ height: "var(--alto-nav)", gap: "var(--e4)" }}
        >
          <NavLink to="/panel" className="fila g2" aria-label="Zociety Cycling Lab">
            <img src="/isotype.png" alt="" width={30} height={30} style={{ objectFit: "contain" }} />
            <span
              style={{
                fontFamily: "var(--fuente-display)",
                fontSize: "var(--texto-lg)",
                letterSpacing: "var(--track-display)",
              }}
            >
              ZOCIETY
            </span>
          </NavLink>

          <nav className="fila g1 nav-escritorio" aria-label="Principal">
            {ENLACES.map(({ a, texto, Icono }) => (
              <NavLink
                key={a}
                to={a}
                className={({ isActive }) => `btn btn-sm ${isActive ? "btn-contorno" : "btn-fantasma"}`}
              >
                <Icono size={16} aria-hidden />
                {texto}
              </NavLink>
            ))}
            {esStaff && (
              <NavLink to="/admin" className="btn btn-sm btn-fantasma">
                Portal Ztaff
              </NavLink>
            )}
          </nav>

          <div className="fila g1">
            <button
              className="btn btn-icono btn-fantasma"
              onClick={alternar}
              aria-label={tema === "oscuro" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
            >
              {tema === "oscuro" ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            <button
              className="btn btn-icono btn-fantasma"
              onClick={() => setNotisAbiertas(true)}
              aria-label={`Notificaciones${sinLeer ? `, ${sinLeer} sin leer` : ""}`}
              style={{ position: "relative" }}
            >
              <Bell size={18} />
              {sinLeer > 0 && <span className="punto-aviso">{sinLeer > 9 ? "9+" : sinLeer}</span>}
            </button>

            <button
              className="btn btn-icono btn-fantasma"
              onClick={() => setAbierto(true)}
              aria-label={`Carrito${cantidadTotal ? `, ${cantidadTotal} artículos` : " vacío"}`}
              style={{ position: "relative" }}
            >
              <ShoppingCart size={18} />
              {cantidadTotal > 0 && <span className="punto-aviso">{cantidadTotal}</span>}
            </button>

            <button
              className="btn btn-icono btn-fantasma solo-movil"
              onClick={() => setMenuAbierto((v) => !v)}
              aria-label="Menú"
              aria-expanded={menuAbierto}
            >
              {menuAbierto ? <X size={18} /> : <Menu size={18} />}
            </button>

            <button className="btn btn-icono btn-fantasma nav-escritorio" onClick={cerrarSesion} aria-label="Salir">
              <LogOut size={18} />
            </button>
          </div>
        </div>

        {menuAbierto && (
          <nav className="contenedor pila g1 solo-movil" style={{ paddingBottom: "var(--e4)" }}>
            {ENLACES.map(({ a, texto, Icono }) => (
              <NavLink
                key={a}
                to={a}
                onClick={() => setMenuAbierto(false)}
                className={({ isActive }) =>
                  `btn ${isActive ? "btn-contorno" : "btn-fantasma"} btn-bloque`
                }
                style={{ justifyContent: "flex-start" }}
              >
                <Icono size={16} aria-hidden />
                {texto}
              </NavLink>
            ))}
            {esStaff && (
              <NavLink
                to="/admin"
                onClick={() => setMenuAbierto(false)}
                className="btn btn-fantasma btn-bloque"
                style={{ justifyContent: "flex-start" }}
              >
                Portal Ztaff
              </NavLink>
            )}
            <button
              className="btn btn-fantasma btn-bloque"
              onClick={cerrarSesion}
              style={{ justifyContent: "flex-start" }}
            >
              <LogOut size={16} aria-hidden />
              Cerrar sesión
            </button>
          </nav>
        )}
      </header>

      <AvisoVerificarCorreo />

      {perfil?.estado === "restricted" && (
        <div className="contenedor" style={{ paddingTop: "var(--e4)" }}>
          <div className="aviso aviso-error" role="alert">
            <span>
              Tu cuenta está restringida por una multa pendiente. Puedes navegar y liquidarla, pero no
              reservar clases hasta ponerte al corriente.
            </span>
          </div>
        </div>
      )}

      <main id="contenido" className="contenedor crece" style={{ paddingBlock: "var(--e6)" }}>
        {children}
      </main>

      <footer className="contenedor" style={{ paddingBlock: "var(--e5)", borderTop: "1px solid var(--borde)" }}>
        <p className="diminuto tenue" style={{ textAlign: "center" }}>
          Zociety Cycling Lab · Precios en MXN ·{" "}
          <Link to="/aviso-de-privacidad" style={{ color: "inherit" }}>
            Aviso de privacidad
          </Link>
        </p>
      </footer>

      <CarritoDrawer />
      {notisAbiertas && <PanelNotificaciones onCerrar={() => setNotisAbiertas(false)} />}

      <style>{`
        .punto-aviso {
          position: absolute;
          top: 0; right: 0;
          min-width: 17px; height: 17px;
          padding: 0 4px;
          display: grid; place-items: center;
          background: var(--acento); color: #fff;
          font-size: 10px; font-weight: 700;
          border-radius: var(--radio-full);
          border: 2px solid var(--fondo);
        }
        .solo-movil { display: none; }
        /*
         * Seis destinos más el portal de Ztaff ya no caben junto a los cuatro
         * botones de icono. En vez de esconderlos tras un desplegable en
         * pantallas donde sí caben, la barra completa cede antes el paso al
         * menú de hamburguesa, que ya existía para el móvil.
         */
        @media (max-width: 1150px) {
          .nav-escritorio { display: none; }
          .solo-movil { display: inline-flex; }
          nav.solo-movil { display: flex; }
        }
      `}</style>
    </div>
  );
}
